import React, { useState, useEffect } from 'react';
import { Download, Share, PlusSquare, X, Smartphone, ArrowUp } from 'lucide-react';

const PWAInstallPrompt = ({ className = "" }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    // 1. Verifica Modo App (Standalone)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               window.navigator.standalone || 
                               document.referrer.includes('android-app://');
    setIsStandalone(isInStandaloneMode);

    // 2. Detecta iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIosDevice);

    // 3. Captura evento Android (Prioridade para o global do App.js)
    if (window.deferredPrompt) {
        setDeferredPrompt(window.deferredPrompt);
    }
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.deferredPrompt = e;
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // 4. DETECÇÃO DE RETORNO DO REDIRECIONAMENTO (IOS)
    const params = new URLSearchParams(window.location.search);
    // 🔹 CORREÇÃO: Usamos isIosDevice aqui pois ele está no escopo local desta função
    if (params.get('install_mode') === 'true' && isIosDevice) {
        setShowInstructions(true);
        // Limpa o parametro visualmente mantendo o municipioId
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('install_mode');
        window.history.replaceState({}, '', newUrl.toString());
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // 🔹 CORREÇÃO CRÍTICA: Array de dependências VAZIO. 
    // A variável 'isIosDevice' é local e não pode estar aqui.
  }, []); 

  const handleInstallClick = async () => {
    // --- LÓGICA ANDROID ---
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          setDeferredPrompt(null);
          window.deferredPrompt = null;
        }
      } catch (err) {
        console.error("Erro Android:", err);
        startIOSFlow(); // Fallback
      }
    } else {
      // --- LÓGICA IOS ---
      startIOSFlow();
    }
  };

  const startIOSFlow = () => {
      // ⚡ O SEGREDO DO IOS: A URL NA BARRA DE ENDEREÇOS TEM QUE TER O ID ⚡
      const savedId = localStorage.getItem('sisfol_municipio_persist');
      const currentParams = new URLSearchParams(window.location.search);
      const hasIdInUrl = currentParams.has('municipioId');
      
      // Se estamos no iOS e a URL NÃO tem o ID visível agora
      if (isIOS && savedId && !hasIdInUrl) {
          // Precisamos recarregar a página para a URL correta.
          const baseUrl = window.location.origin + '/login'; 
          const cleanLink = `${baseUrl}?municipioId=${savedId}&install_mode=true`;
          
          window.location.href = cleanLink;
          return;
      }

      // Se já estamos na URL certa, só abre o modal
      setShowInstructions(true);
  };

  if (!mounted || isStandalone) return null;

  return (
    <>
      <button
        onClick={handleInstallClick}
        className={`
            flex items-center gap-2 
            bg-blue-600 hover:bg-blue-700 active:bg-blue-800
            text-white px-3 py-1.5 rounded-lg 
            shadow-sm hover:shadow transition-all duration-200 
            font-semibold text-xs sm:text-sm 
            whitespace-nowrap z-50
            ${className}
        `}
      >
        <Smartphone size={16} />
        <span className="hidden sm:inline">Instalar App</span>
        <span className="sm:hidden">Instalar</span>
      </button>

      {/* Modal Instruções iOS */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-end sm:items-center justify-center p-4 animate-fade-in">
          <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl w-full max-w-sm p-6 shadow-2xl relative animate-slide-in-up border-t border-gray-200 dark:border-gray-800">
            
            <button 
              onClick={() => setShowInstructions(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-6">
              <div className="mx-auto w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4 shadow-inner">
                {isIOS ? <ArrowUp size={28} className="text-blue-500 animate-bounce" /> : <Download size={28} className="text-blue-500" />}
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Instalar Aplicativo</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed px-2">
                {isIOS 
                  ? "Importante: Siga exatamente os passos abaixo para o aplicativo funcionar corretamente." 
                  : "Para instalar, use o menu do navegador."}
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50">
                <div className="mt-1 text-blue-600 dark:text-blue-400">
                  {isIOS ? <Share size={24} /> : <span className="font-bold text-lg">1.</span>}
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {isIOS ? "1. Toque em Compartilhar" : "Abra o Menu"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {isIOS 
                      ? "O ícone quadrado com seta na barra inferior." 
                      : "Três pontinhos no canto superior."}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50">
                <div className="mt-1 text-blue-600 dark:text-blue-400">
                  <PlusSquare size={24} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800 dark:text-gray-200">
                    {isIOS ? "2. Adicionar à Tela de Início" : "Instalar Aplicativo"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {isIOS 
                      ? "Role as opções para cima até achar este botão." 
                      : "Selecione na lista."}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/30">
                <div className="mt-1 text-blue-600 dark:text-blue-400 font-bold">OK</div>
                <div>
                  <p className="text-sm font-bold text-blue-800 dark:text-blue-200">
                    3. Confirmar "Adicionar"
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-300 mt-0.5">
                    Toque em "Adicionar" no canto superior direito.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 text-center">
                <p className="text-[10px] text-gray-400 uppercase tracking-wide font-medium text-green-600 dark:text-green-400">
                    ✓ Vínculo com município configurado
                </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallPrompt;